/** Formatting shared by all three programs. */

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return "—";
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

export function relativeYears(year: number | null | undefined): string {
  if (!year) return "";
  const now = new Date().getUTCFullYear();
  const d = now - year;
  if (d <= 0) return "this year";
  if (d === 1) return "last year";
  return `${d} years ago`;
}

export function relativeDays(iso: string | null | undefined): string {
  if (!iso) return "";
  const days = Math.floor((Date.now() - Date.parse(iso + "T00:00:00Z")) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.round(days / 30)} months ago`;
  const y = (days / 365).toFixed(days < 730 ? 0 : 1).replace(/\.0$/, "");
  return `${y} year${y === "1" ? "" : "s"} ago`;
}

/**
 * A measurement rendered at a sensible precision for its magnitude. Water
 * chemistry in these bases spans chlorophyll-a at 0.13 µg/L to conductivity
 * in the hundreds, so a fixed number of decimals is wrong at one end or the
 * other.
 */
export function formatValue(v: number | null | undefined, digits?: number): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  if (digits !== undefined) return v.toFixed(digits);
  const a = Math.abs(v);
  if (a >= 1000) return Math.round(v).toLocaleString();
  if (a >= 100) return v.toFixed(0);
  if (a >= 10) return v.toFixed(1);
  if (a >= 1) return v.toFixed(2);
  if (a === 0) return "0";
  return v.toFixed(3);
}

export const plural = (n: number, one: string, many = one + "s") =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Mean of the values that are actually present. */
export function mean(xs: (number | null | undefined)[]): number | null {
  const v = xs.filter((x): x is number => x !== null && x !== undefined && Number.isFinite(x));
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
}

/** Ordinary least squares slope of y against x, or null if under-determined.
 *  Used for the plain-language trend read-out on a station. */
export function slope(pts: { x: number; y: number }[]): number | null {
  if (pts.length < 3) return null;
  const n = pts.length;
  const mx = pts.reduce((a, p) => a + p.x, 0) / n;
  const my = pts.reduce((a, p) => a + p.y, 0) / n;
  let num = 0, den = 0;
  for (const p of pts) { num += (p.x - mx) * (p.y - my); den += (p.x - mx) ** 2; }
  return den === 0 ? null : num / den;
}
